// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — every colour that carries meaning, in one place so a test can hold it to a number.
//
// THE COLOURS LIVE HERE AND NOT IN THE RENDERER, because a hex literal inline in a draw call is a
// colour no contrast test can see. A sibling game shipped walls at 1.14:1 against their background —
// drawn, correctly sized, in the right cells, and completely invisible — and every screenshot looked
// atmospheric. tests/contrast.test.ts holds each of these to >= 3:1 (WCAG 2.1 1.4.11, the floor for
// a non-text graphic) against every surface it can actually sit on.
//
// GAUGE IS CARRIED BY STROKE SHAPE, NOT ONLY COLOUR. Culvert is a thick band with a lighter bore
// line down the middle; filament is a thin double hairline with a bead at the cell centre. Print the
// plate in greyscale and the two are still instantly distinguishable, which is the actual test of a
// colour-blind-safe design — not whether the two hues happen to differ in hue.

/** Surfaces something can be drawn ON. Every meaningful ink is measured against all of them. */
export const SURFACE = {
  /** The page behind the plate. */
  page: '#EEF1F4',
  /** A cell's fill. */
  cell: '#F7F9FB',
  /** A cell tinted as a legal destination while a fitting is armed. */
  legal: '#E4EEF6',
  /** A cell tinted as a costly-but-legal destination (Seep). */
  costly: '#FBE9DE',
  /** The page BEHIND the plate — everything that is not the play surface. Inverts with the theme. */
  shellLight: '#DFE4EA',
  shellDark: '#191C17',
} as const;

export const SURFACES: readonly string[] = [SURFACE.page, SURFACE.cell, SURFACE.legal, SURFACE.costly];

/** The two shells the menu, lobby, results and footer are painted on. */
export const SHELLS: readonly string[] = [SURFACE.shellLight, SURFACE.shellDark];

/** Inks that carry meaning. Each must clear 3:1 on every surface above. */
export const INK = {
  /** Culvert line-work. */
  culvert: '#123A5E',
  /** Filament line-work. */
  filament: '#8A2E0B',
  /** The plate's frame and cell register ticks — this is what makes cell-vs-page legible. */
  frame: '#4A5568',
  /** A vent nobody has sealed yet. */
  ventOpen: '#146B5E',
  /** A vent you have sealed. */
  ventMine: '#8A5A06',
  /** A vent a rival sealed first. */
  ventTheirs: '#5B6472',
  /** A refusal, and the ENDS debt when it is growing. */
  bad: '#B3261E',
  /** Body text. */
  text: '#1A1B24',
  /** Secondary text. */
  dim: '#4A5568',
} as const;

/**
 * Ink for everything painted on the SHELL rather than on the plate — the menu, the lobby, the
 * results table, the footer.
 *
 * THESE EXIST BECAUSE THE PLATE INKS ARE WRONG HERE, and shipping them was a real bug. The design
 * keeps the plate light in both themes and inverts only the shell; the first version did exactly
 * that and left the wordmark, the tagline and the secondary buttons in the plate's dark blue — which
 * on the dark shell is dark-on-dark and barely readable. The contrast gate missed it because it
 * measured every ink against the PLATE's surfaces and never against the shell.
 */
export const SHELL_INK = {
  light: { text: '#1A1B24', dim: '#4A5568', accent: '#123A5E' },
  dark: { text: '#F2F5F8', dim: '#B9C2CC', accent: '#9FC4E8' },
} as const;

export type InkName = keyof typeof INK;

/** Which inks are painted on the play surface and must therefore clear the graphic floor. */
export const ON_PLATE: readonly InkName[] = [
  'culvert',
  'filament',
  'frame',
  'ventOpen',
  'ventMine',
  'ventTheirs',
  'bad',
];

/** Six seat glyphs. Six colours can never be mutually 3:1, so every seat also carries a SHAPE. */
export const SEAT_GLYPH: readonly string[] = ['●', '■', '◆', '▲', '⬢', '✚'];
export const SEAT_INK: readonly string[] = ['#123A5E', '#8A2E0B', '#146B5E', '#6B21A8', '#8A5A06', '#9F1239'];

// ── contrast maths, used by the test and by nothing else at runtime ──────────────────────────────

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const v = [0, 2, 4].map((i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Composite a colour at some alpha over a background, so the test measures what is actually PAINTED.
 *
 * The ghost is drawn semi-transparent, and measuring its raw hex would certify a contrast the player
 * never sees.
 */
export function composite(fg: string, bg: string, alpha: number): string {
  const f = fg.replace('#', '');
  const b = bg.replace('#', '');
  const mix = [0, 2, 4].map((i) => {
    const fv = parseInt(f.slice(i, i + 2), 16);
    const bv = parseInt(b.slice(i, i + 2), 16);
    return Math.round(fv * alpha + bv * (1 - alpha));
  });
  return `#${mix.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** The alpha the armed ghost is drawn at. Exported so the test and the CSS cannot disagree. */
export const GHOST_ALPHA = 0.45;
